import { apiCall, apiUpload } from './client';
/**
 * Domain endpoint wrappers. One per resource — never call apiCall from screens.
 * Types come from packages/shared where they exist; the admin-only shapes
 * live here as ad-hoc interfaces (they are internal to the panel).
 */
// -------- Auth — admin-only endpoints. Server refuses to send a code for
// non-admin emails on request-code, and refuses to sign a token for non-
// admins on verify (403). See auth.route.ts for the security rationale.
export function requestCode(email) {
    return apiCall('/admin/auth/request-code', {
        method: 'POST', body: { email }, auth: false,
    });
}
export function verifyCode(email, code) {
    return apiCall('/admin/auth/verify', {
        method: 'POST', body: { email, code }, auth: false,
    });
}
export const listGrades = () => apiCall('/admin/grades');
export const createGrade = (b) => apiCall('/admin/grades', { method: 'POST', body: b });
export const updateGrade = (id, b) => apiCall(`/admin/grades/${id}`, { method: 'PATCH', body: b });
export const deleteGrade = (id) => apiCall(`/admin/grades/${id}`, { method: 'DELETE' });
export const reorderGrades = (ids) => apiCall('/admin/grades/reorder', { method: 'PUT', body: { ids } });
export const listTracks = (gradeId) => apiCall(`/admin/grades/${gradeId}/tracks`);
export const createTrack = (b) => apiCall('/admin/tracks', { method: 'POST', body: b });
export const updateTrack = (id, b) => apiCall(`/admin/tracks/${id}`, { method: 'PATCH', body: b });
export const deleteTrack = (id) => apiCall(`/admin/tracks/${id}`, { method: 'DELETE' });
export const reorderTracks = (gradeId, ids) => apiCall(`/admin/grades/${gradeId}/tracks/reorder`, { method: 'PUT', body: { ids } });
export const listLessons = (trackId) => apiCall(`/admin/tracks/${trackId}/lessons`);
// recommended_order intentionally not part of create/update surface here —
// it's owned by the dedicated "recommended path" screen (per-grade,
// cross-track). Lessons default to recommended_order=NULL on create; the
// admin drags them into the path on that screen.
export const createLesson = (b) => apiCall('/admin/lessons', { method: 'POST', body: b });
export const updateLesson = (id, b) => apiCall(`/admin/lessons/${id}`, { method: 'PATCH', body: b });
export const deleteLesson = (id) => apiCall(`/admin/lessons/${id}`, { method: 'DELETE' });
export const reorderLessons = (trackId, ids) => apiCall(`/admin/tracks/${trackId}/lessons/reorder`, { method: 'PUT', body: { ids } });
export const getRecommendedPath = (gradeId) => apiCall(`/admin/grades/${gradeId}/recommended-order`);
/**
 * Save the recommended path. `included_ids` is the ordered list; the server
 * writes 10, 20, 30 … for those and NULL for every other lesson in the grade.
 * `force=true` overrides the DAG-contradiction warning (spec 2.2).
 */
export const saveRecommendedPath = (gradeId, included_ids, force = false) => apiCall(`/admin/grades/${gradeId}/recommended-order`, {
    method: 'PUT', body: { included_ids, force },
    // The editor surfaces recommended_order_contradicts_dag with a
    // controlled confirm dialog (show contradictions + "שמור בכל זאת"),
    // so opt that specific code out of the auto-toast; anything else
    // (missing_field on the id list etc.) still surfaces by default.
    handled: ['recommended_order_contradicts_dag'],
});
export const listClips = (lessonId) => apiCall(`/admin/lessons/${lessonId}/clips`);
export const createClip = (b) => apiCall('/admin/clips', { method: 'POST', body: b });
export const updateClip = (id, b) => apiCall(`/admin/clips/${id}`, { method: 'PATCH', body: b });
export const deleteClip = (id) => apiCall(`/admin/clips/${id}`, { method: 'DELETE' });
export const reorderClips = (lessonId, ids) => apiCall(`/admin/lessons/${lessonId}/clips/reorder`, { method: 'PUT', body: { ids } });
export const getClipUploadToken = (clipId) => apiCall(`/admin/clips/${clipId}/upload-token`, { method: 'POST' });
export const listPrereqs = (target, targetId) => apiCall(`/admin/prerequisites?target=${target}&target_id=${targetId}`);
export const createPrereq = (b) => apiCall('/admin/prerequisites', { method: 'POST', body: b });
export const deletePrereq = (id) => apiCall(`/admin/prerequisites/${id}`, { method: 'DELETE' });
export const getPrereqCandidates = (target, targetId) => apiCall(`/admin/prereqs/candidates?target=${target}&target_id=${targetId}`);
export const listTemplates = () => apiCall('/admin/question-templates');
export const getTemplate = (id) => apiCall(`/admin/question-templates/${id}`);
export const createTemplate = (b) => apiCall('/admin/question-templates', { method: 'POST', body: b });
export const updateTemplate = (id, b) => apiCall(`/admin/question-templates/${id}`, { method: 'PATCH', body: b });
export const deleteTemplate = (id) => apiCall(`/admin/question-templates/${id}`, { method: 'DELETE' });
// The rule-set create/update endpoints have two "warning-as-error" codes
// (rule_syntax + unsatisfiable_rules) that the modal surfaces inline —
// per-line errors + a controlled confirm dialog. We opt those two codes
// out of the client's auto-toast so the modal owns their UX. Every OTHER
// code (missing_field, network, etc.) still auto-surfaces by default.
const RULE_SET_HANDLED = ['rule_syntax', 'unsatisfiable_rules'];
export const createRuleSet = (b) => apiCall('/admin/rule-sets', {
    method: 'POST', body: b, handled: RULE_SET_HANDLED,
});
/**
 * Patch an existing set — server touches only what is sent. When `rules` is
 * provided, it fully replaces the set's existing rules atomically. Omit
 * `rules` to leave them alone. `force` matches the create endpoint's semantic.
 */
export const updateRuleSet = (id, b) => apiCall(`/admin/rule-sets/${id}`, {
    method: 'PATCH', body: b, handled: RULE_SET_HANDLED,
});
export const deleteRuleSet = (id) => apiCall(`/admin/rule-sets/${id}`, { method: 'DELETE' });
export const createRule = (b) => apiCall('/admin/rules', { method: 'POST', body: b });
export const deleteRule = (id) => apiCall(`/admin/rules/${id}`, { method: 'DELETE' });
export const dryRunTemplate = (b) => apiCall('/admin/dry-run-template', { method: 'POST', body: b });
export const getContentTree = () => apiCall('/admin/content-tree');
export const listAssessments = () => apiCall('/admin/assessments');
export const getAssessment = (id) => apiCall(`/admin/assessments/${id}`);
// POST supports the atomic "create + configure" path — passing `levels`
// inline commits everything in a single server-side transaction, so an
// abandoned drawer never leaves an orphan assessment behind.
export const createAssessment = (b) => apiCall('/admin/assessments', { method: 'POST', body: b });
export const updateAssessmentName = (id, name) => apiCall(`/admin/assessments/${id}`, { method: 'PATCH', body: { name } });
export const putAssessmentStructure = (id, levels) => apiCall(`/admin/assessments/${id}/structure`, { method: 'PUT', body: { levels } });
export const deleteAssessment = (id) => apiCall(`/admin/assessments/${id}`, { method: 'DELETE' });
// Link/transfer/detach a passing assessment to/from a lesson. Server enforces
// kind=passing and one-passing-per-lesson (uq_assessments_lesson). Pass null
// to detach. On 409, ApiError.details carries `current_assessment_id`.
export const updateAssessmentLessonLink = (id, lesson_id) => apiCall(`/admin/assessments/${id}/lesson-link`, {
    method: 'PATCH', body: { lesson_id },
});
export const listCoupons = () => apiCall('/admin/coupons');
export const createCoupon = (b) => apiCall('/admin/coupons', { method: 'POST', body: b });
export const deleteCoupon = (id) => apiCall(`/admin/coupons/${id}`, { method: 'DELETE' });
// PATCH: currently just `active` (in-table quick-toggle) and `expires_at`
// (extend/shorten). Code/discount/kind are immutable — changing them would
// silently invalidate every distributed coupon, so the API omits them.
export const updateCoupon = (id, b) => apiCall(`/admin/coupons/${id}`, { method: 'PATCH', body: b });
export const listUpsellRules = () => apiCall('/admin/upsell-rules');
export const createUpsellRule = (b) => apiCall('/admin/upsell-rules', { method: 'POST', body: b });
export const updateUpsellRule = (id, b) => apiCall(`/admin/upsell-rules/${id}`, { method: 'PATCH', body: b });
export const deleteUpsellRule = (id) => apiCall(`/admin/upsell-rules/${id}`, { method: 'DELETE' });
export const listConfig = () => apiCall('/admin/config');
export const putConfig = (key, value, description) => apiCall(`/admin/config/${key}`, { method: 'PUT', body: { value, description } });
export const listUsers = () => apiCall('/admin/users');
export const getStudent = (id) => apiCall(`/admin/students/${id}`);
export const getStudentActivity = (id, limit = 50, offset = 0) => apiCall(`/admin/students/${id}/activity?limit=${limit}&offset=${offset}`);
// -------- Image uploads (admin only) --------
// The server stores the file in Tigris and returns a public URL. The
// returned string is what gets written to the template's image_url
// column — same shape as the manual URL input.
export const uploadImage = (file) => {
    const fd = new FormData();
    fd.append('file', file);
    // `missing_file`, `unsupported_media_type`, `file_too_large`, and
    // `storage_unconfigured` are rendered inline by the upload control,
    // so we opt them out of the central auto-toast.
    return apiUpload('/admin/uploads/image', fd, {
        handled: ['missing_file', 'unsupported_media_type', 'file_too_large', 'storage_unconfigured'],
    });
};
// -------- Me (shared endpoint — used for the dashboard greeting) --------
export const getMe = () => apiCall('/me');
export const getStatsSummary = () => apiCall('/admin/stats/summary');
export const getStatsTimeseries = () => apiCall('/admin/stats/timeseries');
export const getStatsRecentStudents = (limit = 10) => apiCall(`/admin/stats/recent-students?limit=${limit}`);
export const getStudentProgress = (userId) => apiCall(`/admin/students/${userId}/progress`);
export const resetStudentLesson = (userId, lessonId) => apiCall(`/admin/students/${userId}/reset-lesson/${lessonId}`, { method: 'POST' });
export const resetStudentAssessment = (userId, assessmentId) => apiCall(`/admin/students/${userId}/reset-assessment/${assessmentId}`, { method: 'POST' });
export const resetStudentGrade = (userId, gradeId) => apiCall(`/admin/students/${userId}/reset-grade/${gradeId}`, { method: 'POST' });
export const getCouponRedemptions = (couponId) => apiCall(`/admin/coupons/${couponId}/redemptions`);
// -------- Admin user guide (HTML served inside an iframe srcDoc) --------
// apiCall falls back to the raw response text when JSON.parse fails
// (see client.ts), so a plain string generic is the right return shape.
export const getAdminGuide = () => apiCall('/admin/guide');
export const getStatsTopLessons = (windowDays = 7, limit = 5) => apiCall(`/admin/stats/top-lessons?window=${windowDays}d&limit=${limit}`);
